import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CompanyOption, companiesList, errorText } from '../accounting.api';

const STORAGE_KEY = 'accounting.companyId';

function stored(): number | null {
  try {
    const v = Number(localStorage.getItem(STORAGE_KEY));
    return v > 0 ? v : null;
  } catch {
    return null;
  }
}

/**
 * The company whose books every accounting screen shows. The choice is remembered across
 * screens and visits; without one (or with one that no longer exists) it is the first company.
 */
export function useBooksCompany() {
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [companyId, setId] = useState<number | null>(stored());
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    companiesList()
      .then((list) => {
        const all = list ?? [];
        setCompanies(all);
        setId((cur) => (cur && all.some((c) => c.id === cur) ? cur : (all[0]?.id ?? null)));
      })
      .catch((e) => toast.error(errorText(e, 'Could not load companies', 'ACCOUNTING_COMPANIES_FAILED')))
      .finally(() => setLoaded(true));
  }, []);

  const setCompanyId = (id: number) => {
    setId(id);
    try {
      localStorage.setItem(STORAGE_KEY, String(id));
    } catch {
      /* storage unavailable: this screen only */
    }
  };

  // Only once the list confirms it, so a stale stored id never reaches the API.
  const ready = loaded && companyId !== null && companies.some((c) => c.id === companyId);
  return { companies, companyId: ready ? companyId : null, setCompanyId, loaded };
}

export default function CompanySelect({
  companies,
  value,
  onChange,
}: {
  companies: CompanyOption[];
  value: number | null;
  onChange: (id: number) => void;
}) {
  return (
    <TextField
      select
      size='small'
      label='Company'
      value={value ?? ''}
      onChange={(e) => onChange(Number(e.target.value))}
      sx={{ minWidth: { xs: 180, sm: 240 }, maxWidth: '100%' }}
    >
      {companies.map((c) => (
        <MenuItem key={c.id} value={c.id}>
          {c.name}
        </MenuItem>
      ))}
    </TextField>
  );
}
